import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { PlusIcon } from 'lucide-react';
import { faqItems } from '../data/methodology';
import { sourceIndex } from '../utils/sourceIndex';
import { PageHeader } from '../components/PageHeader';
import { RichTextView } from '../components/RichTextView';
import { DataBadge } from '../components/DataBadge';
import { COMPILED_LABEL } from '../data/sources';
import { Link } from 'react-router-dom';
import { Grade, GRADE_FOR_KIND, GRADE_INFO } from '../utils/grades';
export function Methodology() {
  const [open, setOpen] = useState<string[]>([faqItems[0].id]);
  const toggle = (id: string) => setOpen(o => o.includes(id) ? o.filter(x => x !== id) : [...o, id]);
  const external = sourceIndex.filter(s => !s.url.startsWith('/'));
  const internal = sourceIndex.filter(s => s.url.startsWith('/'));
  return <>
      <PageHeader title="Methodology" lede="How the count is made, where it is weak, and every source it rests on. The numbered markers across the site point to the index at the bottom of this page." data-id="element-1270" data-mp="22wa1k24ym3b">
        <DataBadge mode="snapshot" source="Methodology" asOf={COMPILED_LABEL} data-id="element-1271" data-mp="305mp7wrwggar" />
      </PageHeader>

      <div className="mx-auto grid max-w-[1400px] gap-12 px-5 pt-10 md:px-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]" data-id="element-1272" data-mp="2w64gdzdcrvoa">
        <section aria-label="Questions" data-id="element-1273" data-mp="2r8kss8urm6jo">
          <ul className="border-t border-line" data-id="element-1274" data-mp="3nxa7xrf0cl1y">
            {faqItems.map(f => {
            const isOpen = open.includes(f.id);
            return <li key={f.id} id={f.id} className="scroll-mt-32 border-b border-line" data-id="element-1275" data-mp="3jx9t45nzy7ud">
                  <h2 data-id="element-1276" data-mp="3f2m1vz1lyzc3">
                    <button type="button" aria-expanded={isOpen} aria-controls={`${f.id}-panel`} onClick={() => toggle(f.id)} className="flex w-full items-center justify-between gap-4 py-5 text-left text-[17px] text-ink" data-id="element-1277" data-mp="3a5dixw8myr9s">
                      {f.question}
                      <PlusIcon className={`h-4 w-4 shrink-0 text-muted transition-transform duration-200 ease-out ${isOpen ? 'rotate-45' : ''}`} aria-hidden="true" data-id="element-1278" data-mp="pn8bu7zfe2gv" />
                    </button>
                  </h2>
                  <AnimatePresence initial={false} data-id="element-1279" data-mp="ktxc0qt16lyg">
                    {isOpen && <motion.div id={`${f.id}-panel`} initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.22,
                  ease: [0.23, 1, 0.32, 1]
                }} className="overflow-hidden" data-id="element-1280" data-mp="6ue5g2fvbio9">
                        <div className="space-y-3 pb-6 text-[14.5px] leading-relaxed text-ink/85" data-id="element-1281" data-mp="1s77i33m6evam">
                          {f.paragraphs.map((p, i) => <p key={i} data-id="element-1282" data-mp="1w786nh2zomve" data-mpi={i}>
                              <RichTextView text={p} data-id="element-1283" data-mp="3gqv5j1v48gh4" />
                            </p>)}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </li>;
          })}
          </ul>
        </section>

        <section id="sources" aria-labelledby="sources-title" className="scroll-mt-32" data-id="element-1284" data-mp="3lq7vhvhaa3nx">
          <div className="flex items-baseline justify-between" data-id="element-1285" data-mp="49geh8bq32t1">
            <h2 id="sources-title" className="font-serif text-2xl text-ink" data-id="element-1286" data-mp="4bsydr11jn8q">
              Data sources index
            </h2>
            <span className="font-mono text-[12px] text-muted" data-id="element-1287" data-mp="db690n0wihv1">{sourceIndex.length} entries</span>
          </div>
          <p className="mt-1 text-[13px] text-muted" data-id="element-1288" data-mp="8bbi4jh4ls9z">Generated from every source referenced across the site. Numbers match the markers.</p>

          <div id="grades" className="mt-6 scroll-mt-32 rounded-lg border border-line bg-surface p-4" data-id="element-1289" data-mp="2f29jncasjwfl">
            <h3 className="text-[14px] font-medium text-ink" data-id="element-1290" data-mp="2a510p9htjoda">Confidence grades</h3>
            <p className="mt-1 text-[12.5px] text-muted" data-id="element-1291" data-mp="2ch1q7v4ezgxs">Every source marker carries a small letter after its number. Hover any marker for its grade, source and a “Challenge this number” link.</p>
            <dl className="mt-3 space-y-2" data-id="element-1292" data-mp="2lisy96mffzut">
              {(Object.keys(GRADE_INFO) as Grade[]).map(g => <div key={g} className="grid grid-cols-[1.5rem_minmax(0,1fr)] gap-2" data-id="element-1293" data-mp="2qgkxymtbcq9k">
                  <dt className={`font-mono text-[14px] font-semibold ${GRADE_INFO[g].className}`} data-id="element-1294" data-mp="1sv6w82sul3a2">{g}</dt>
                  <dd className="text-[13px] text-ink/85" data-id="element-1295" data-mp="1xsym6r9xmfbm">
                    <span className="text-ink" data-id="element-1296" data-mp="22ri7qqkm5p3g">{GRADE_INFO[g].label}</span> — {GRADE_INFO[g].detail}
                  </dd>
                </div>)}
            </dl>
            <p className="mt-3 border-t border-line pt-3 text-[12.5px] text-muted" data-id="element-1297" data-mp="1a34d8ksqhawn">
              Snapshots older than 90 days show an amber “needs refresh” badge and turn red after 180 days. Confirmed corrections are dated in the{' '}
              <Link to="/changelog" className="text-ink underline decoration-line underline-offset-2" data-id="element-1298" data-mp="17hn3k7r462mc">
                Census Bulletin
              </Link>
              .
            </p>
          </div>
          <ol className="mt-5 divide-y divide-line border-y border-line" data-id="element-1299" data-mp="xj7aw0cfulaq">
            {external.map(s => <li key={s.n} id={`src-${s.n}`} className="grid grid-cols-[2.5rem_minmax(0,1fr)] gap-2 py-2.5" data-id="element-1300" data-mp="skw0noi8e8rz">
                <span className="font-mono text-[12px] text-machine" data-id="element-1301" data-mp="ol54t6dul2gl">
                  [{s.n}]<span className={`ml-0.5 text-[10px] ${GRADE_INFO[GRADE_FOR_KIND[s.kind as keyof typeof GRADE_FOR_KIND] ?? 'B'].className}`} data-id="element-1302" data-mp="jsbzohegvs3z">{GRADE_FOR_KIND[s.kind as keyof typeof GRADE_FOR_KIND] ?? 'B'}</span>
                </span>
                <div className="min-w-0" data-id="element-1303" data-mp="9stjmc1ogqh7">
                  <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-[13.5px] text-ink hover:text-machine" data-id="element-1304" data-mp="4w30tgyhngs3">
                    {s.name}
                  </a>
                  <p className="truncate font-mono text-[11px] text-faint" data-id="element-1305" data-mp="3mes3wlq0psg9">
                    {s.publisher} · {s.kind}
                  </p>
                </div>
              </li>)}
          </ol>
          <h3 className="mt-8 text-[14px] font-medium text-ink" data-id="element-1306" data-mp="3dedfhdhrbx8j">Our own estimates</h3>
          <ol className="mt-3 divide-y divide-line border-y border-line" data-id="element-1307" data-mp="3icnbtrf7x3vq">
            {internal.map(s => <li key={s.n} className="grid grid-cols-[2.5rem_minmax(0,1fr)] gap-2 py-2.5" data-id="element-1308" data-mp="9wlljtk2937p">
                <span className="font-mono text-[12px] text-machine" data-id="element-1309" data-mp="dz8exywdqw70">[{s.n}]</span>
                <span className="text-[13.5px] text-ink/85" data-id="element-1310" data-mp="7bpk2m3j0eb">{s.name}</span>
              </li>)}
          </ol>
        </section>
      </div>
    </>;
}